import "@/App.css";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, ClipboardList, Search, MessagesSquare, Route } from "lucide-react";
import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider, useSchedule } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { RecruitingForm } from "@/components/RecruitingForm";

const RECRUITING_BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/EYhG0bXyJiuo6Udn6mEr";

const STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    title: "Submit Your Application",
    body: "Complete the application below so we can learn more about your background and goals.",
  },
  {
    n: "02",
    icon: Search,
    title: "Application Review",
    body: "We'll personally review your application to determine whether your experience and goals align with our agency.",
  },
  {
    n: "03",
    icon: MessagesSquare,
    title: "Introductory Conversation",
    body: "Qualified applicants will be invited to schedule a relaxed, no-pressure conversation to learn more about the opportunity.",
  },
  {
    n: "04",
    icon: Route,
    title: "Next Steps",
    body: "If it's a mutual fit, we'll discuss licensing, training, contracting, and the onboarding process.",
  },
];

function ApplyContent() {
  const { openSchedule } = useSchedule();

  return (
    <main>
      {/* Hero */}
      <section data-testid="apply-hero" className="relative min-h-[60vh] flex flex-col justify-end pb-16 lg:pb-24 px-6 lg:px-10 max-w-[1400px] mx-auto pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#d4af37]" />
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Careers · Application</span>
        </motion.div>

        <Reveal>
          <h1 className="serif font-light leading-[0.98] text-[#f4f0ea] text-[10vw] sm:text-[8vw] lg:text-[5.5vw] max-w-5xl">
            Apply to Join <span className="italic text-[#d4af37]">John Campbell Insurance</span>
          </h1>
        </Reveal>

        <Reveal delay={0.2} className="mt-10 max-w-2xl space-y-6">
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            If you're interested in building a meaningful independent insurance business focused on serving families,
            we'd love to learn more about you.
          </p>
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            Complete the application below and we'll carefully review your information. Qualified candidates will be
            invited to an Introductory Conversation to determine whether this opportunity is the right fit for both of us.
          </p>
        </Reveal>

        <Reveal delay={0.3} className="mt-10">
          <a
            href="/join"
            data-testid="apply-back-join"
            className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] transition-colors duration-400"
          >
            <ArrowLeft size={15} className="transition-transform duration-400 group-hover:-translate-x-1" />
            Back to Join Our Team
          </a>
        </Reveal>
      </section>

      {/* What Happens Next */}
      <section data-testid="apply-steps" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">The Process</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-14 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              What happens <span className="italic text-[#d4af37]">next</span>.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div
                  data-testid={`apply-step-${i + 1}`}
                  className="relative h-full bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-white/10 rounded-sm p-8 overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
                  <div className="flex items-center justify-between mb-8">
                    <span className="h-11 w-11 rounded-full border border-[#d4af37]/30 flex items-center justify-center">
                      <s.icon size={18} className="text-[#d4af37]" />
                    </span>
                    <span className="mono text-sm text-[#d4af37]/60">{s.n}</span>
                  </div>
                  <h3 className="serif text-xl lg:text-2xl text-[#f4f0ea] mb-4 leading-snug">{s.title}</h3>
                  <p className="text-[#a3a3a3] text-sm leading-relaxed tracking-wide">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section data-testid="apply-form-section" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Application</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Reveal className="mb-10 max-w-3xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl mb-8">
              Tell us about <span className="italic text-[#d4af37]">yourself</span>.
            </h2>
            <div className="space-y-5 text-[#a3a3a3] text-base leading-relaxed tracking-wide">
              <p>Every application is personally reviewed by John Campbell.</p>
              <p>
                My goal isn't simply to recruit agents—it's to build long-term relationships with people who are
                passionate about serving families with integrity.
              </p>
              <p>If that sounds like you, I'd love to learn more about your background and your goals.</p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              data-testid="apply-form-native"
              className="relative bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-[#d4af37]/20 rounded-sm overflow-hidden p-8 sm:p-10 lg:p-14"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
              <RecruitingForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing Statement */}
      <section data-testid="apply-closing" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
          <Reveal className="max-w-2xl">
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl mb-6">
              Prefer to <span className="italic text-[#d4af37]">Talk First</span>?
            </h2>
            <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
              Not everyone is ready to submit an application right away—and that's perfectly okay. If you'd like to learn
              more about the opportunity, ask questions, or simply get to know me before applying, I'd be happy to have a
              relaxed, no-pressure conversation.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              onClick={() => openSchedule(RECRUITING_BOOKING_URL)}
              data-testid="apply-schedule-btn"
              className="group shrink-0 inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#d4af37] text-[#030303] rounded-full hover:bg-[#f4f0ea] transition-colors duration-400"
            >
              <CalendarDays size={15} /> Schedule an Introductory Conversation
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default function Apply() {
  return (
    <div className="App grain bg-[#030303]">
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <ApplyContent />
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
